import type { MouseEvent, Ref } from 'react';
import { Link, type LinkProps, generatePath } from 'react-router';

import { useMe } from '@/providers/User';
import { ROUTES } from '@/router';

export type InstanceLinkProps = LinkProps & {
  ref?: Ref<HTMLAnchorElement>;
  disabled?: boolean;
};

const InstanceLink = ({
  ref,
  to,
  disabled = false,
  onClick,
  tabIndex,
  ...props
}: InstanceLinkProps) => {
  const user = useMe();
  const instanceSlug = user?.currentOrg.instanceId.replace(/^inst:/, '');
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    onClick?.(event);
  };

  const linkProps = {
    ...props,
    onClick: handleClick,
    tabIndex: disabled ? -1 : tabIndex,
    'aria-disabled': disabled ? true : props['aria-disabled'],
  };

  if (instanceSlug) {
    if (typeof to === 'string' && to.startsWith(ROUTES.instance.path)) {
      return <Link ref={ref} to={generatePath(to, { instance: instanceSlug })} {...linkProps} />;
    } else if (
      typeof to === 'object' &&
      to.pathname &&
      to.pathname.startsWith(ROUTES.instance.path)
    ) {
      return (
        <Link
          ref={ref}
          to={{ ...to, pathname: generatePath(to.pathname, { instance: instanceSlug }) }}
          {...linkProps}
        />
      );
    }
  }

  return <Link ref={ref} to={to} {...linkProps} />;
};

export default InstanceLink;
